import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Tabs from '@components/common/Dashboard/DashboardTabs/Tabs';
import {
  DatePickerWrapper,
  HeaderPanelWrapper,
  HeaderWrapper,
  LeftWrapper,
  RightWrapper,
  StyledButton,
  ToggleButton,
  StyledDivider,
  Title,
} from './styled';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ControlOutlined, HomeOutlined } from '@ant-design/icons';
import { featureSelector, selectedCustomerIdSelector } from '@src/store/common/selectors';
import Export from './Export';
import { useAtom, useSetAtom } from 'jotai';
import { defaultFilters, searchTextAtom, tableFiltersAtom } from '../atoms';
import Search from 'antd/es/input/Search';
import moment from 'moment-timezone';
import { DatePickerModifier } from './DatePickerModifier';
import { debounce } from 'lodash';
import SensitivityControl from './SensitivityControl';
import SensitivityLogs from './SensitivityLogs';
import featureNames, { globalFeatureNames } from '@src/utils/featureToggle/constants';
import AutoInsights from './AutoInsights';
import { Dropdown, Tooltip } from 'antd';
import useAiAlertStore from '@src/globalStore/aiAlertStore';
import DimensionScan from './DimensionScanBtn';
import DimensionScanToDrawer from './DimensionScanBtnToDrawer';
import ToggleIcon from '@icons/common/toggle.svg';
import { OPEN_SENSITIVITY_LOGS } from '@src/utils/sensor/track';
import apptracker from '@src/utils/sensor/apptracker';
import { CURRENT_PRODUCT_MODE, isAppModule, TAB_TITLE_AI_ALERT } from '@src/utils/constants';
import { aiCohortDrawerAtom } from './SensitivityControl/atoms';
import AICohortDrawer from './AICohort';
import { useGlobalFeature } from '@src/globalStore/globalFeatureFlagsStore';
import GlobalMuteBanner from '@src/components/notification-channel/GlobalMute/GlobalMuteBanner';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';
import { useDefaultLandingPageMutation } from '@hooks/useDefaultLandingPage';
import DefaultLandingPrompt from '@components/common/DefaultLandingPage';
import { onSetDefaultLanding } from '@components/common/kebabMenuUtils/defaultLandingPage';

const HeaderPanel = (props) => {
  const { pageType } = props;
  const { id: currentShareId = 'AI Alerts' } = useParams();
  const dispatch = useDispatch();
  const customerId = useSelector(selectedCustomerIdSelector);
  const defaultLandingPage = useDefaultLandingPageStore((state) => ({
    url: state.url,
    path: state.path,
    name: state.name,
  }));
  const { setDefaultLandingPage: mutateLandingPage } = useDefaultLandingPageMutation();
  const isDefaultLandingPage = (defaultLandingPage.path || '').endsWith(window.location.pathname);
  const isAlertPartnerAccount = useSelector(featureSelector('is_alert_forwarding_partner'));
  const { setInterval, interval } = useAiAlertStore((state) => ({
    setInterval: state.setInterval,
    interval: state.interval,
  }));
  const { from, to, shortcut } = interval;

  const [currentInterval, setCurrentInterval] = useState([from, to, shortcut]);
  const [isConfigPopupOpen, setConfigPopupOpen] = useState(false);
  const [isLogsPopupOpen, setLogsPopupOpen] = useState(false);
  const [searchText, setSearchText] = useAtom(searchTextAtom);
  const setFilters = useSetAtom(tableFiltersAtom);
  const [currentSearchText, setCurrentSearchText] = useState(searchText);
  const autoInsightsEnabled = useSelector(featureSelector(featureNames.AUTO_INSIGHTS));
  const enableDPICustomDimension = useGlobalFeature(globalFeatureNames.DPI_CUSTOM_DIMENSION);
  const enableDPIAlertPagination = useGlobalFeature(globalFeatureNames.DPI_ALERT_PAGINATION);
  const [openAICohortDrawer, setOpenAICohortDrawer] = useAtom(aiCohortDrawerAtom);
  const onChangeDatePicker = useCallback(({ interval: updatedInterval, selectedShortcut }) => {
    setCurrentInterval([...updatedInterval, selectedShortcut]);
  }, []);

  const onApplyDatePicker = () => {
    // reset search and filter on interval change
    setSearchText('');
    setCurrentSearchText('');
    setFilters(defaultFilters);

    setInterval({
      from: moment(currentInterval[0]).format(),
      to: moment(currentInterval[1]).format(),
      shortcut: currentInterval[2],
    });
  };
  const onCancelDatePicker = () => setCurrentInterval([from, to, shortcut]);

  const openConfigPopup = () => setConfigPopupOpen(true);
  const closeConfigPopup = () => setConfigPopupOpen(false);

  const openLogsPopup = () => {
    setLogsPopupOpen(true);
    apptracker(OPEN_SENSITIVITY_LOGS, {
      product: CURRENT_PRODUCT_MODE,
    });
  };
  const closeLogsPopup = () => setLogsPopupOpen(false);
  const onApplyMore = ({ key }: any) => {
    switch (key) {
      case 'set-default-landing-page': {
        onSetDefaultLanding(dispatch, defaultLandingPage, customerId, (payload) =>
          mutateLandingPage({ ...payload, name: TAB_TITLE_AI_ALERT }),
        );
        break;
      }
      case 'sensitivity-logs': {
        openLogsPopup();
        break;
      }
    }
  };

  const kebabItems = [
    {
      key: 'set-default-landing-page',
      label: 'Set as landing page',
      icon: <HomeOutlined />,
      disabled: isDefaultLandingPage,
    },
    {
      key: 'sensitivity-logs',
      label: 'Sensitivity Change Logs',
      disabled: false,
    },
  ];

  const onSearch: any = (value) => {
    setSearchText(value);
  };
  const handleSearch = useCallback(
    (value) => {
      onSearch(value.toLowerCase());
    },
    [onSearch],
  );
  const handleChange = useCallback((e) => {
    const value = e.target.value;
    setCurrentSearchText(value);
    // Remove automatic debounced search - only update on Enter or Search button
  }, []);

  // Handle Enter key press for search
  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        handleSearch(currentSearchText);
      }
    },
    [handleSearch, currentSearchText],
  );

  useEffect(() => {
    setCurrentInterval([from, to, shortcut]);
  }, [from, interval, shortcut, to]);

  return (
    <HeaderPanelWrapper>
      <Tabs currentShareId={currentShareId} pageType={pageType} />
      <HeaderWrapper>
        <LeftWrapper>
          <Title>AI Alerts</Title>
          <Search
            style={{ width: '50%' }}
            placeholder="Search Alert Id, Root Cause (Press Enter to search)" // Update placeholder to indicate Enter key
            allowClear
            value={currentSearchText}
            onChange={handleChange}
            onSearch={handleSearch}
            onKeyPress={handleKeyPress}
          />
          {enableDPICustomDimension ? <DimensionScanToDrawer /> : <DimensionScan />}

          {/* AI Cohort Drawer */}
          <AICohortDrawer
            visible={openAICohortDrawer.open}
            onClose={() =>
              setOpenAICohortDrawer({ open: false, metrics: [], key: '', isFlow: false })
            }
            sensitivityConfigDrawerOpen={isConfigPopupOpen}
          />
        </LeftWrapper>
        <RightWrapper>
          <GlobalMuteBanner />
          {autoInsightsEnabled ? (
            <>
              <AutoInsights />
              <StyledDivider type="vertical" />
            </>
          ) : null}

          {!isAlertPartnerAccount ? (
            <>
              <StyledButton
                onClick={openConfigPopup}
                icon={
                  <Tooltip title="Alert Config">
                    <ControlOutlined />
                  </Tooltip>
                }
              />
              {isConfigPopupOpen ? (
                <SensitivityControl isPopupOpen={isConfigPopupOpen} cancelEdit={closeConfigPopup} />
              ) : null}
            </>
          ) : null}

          {isAppModule && enableDPIAlertPagination ? (
            <Export title="Export (Scroll to bottom to export all)" />
          ) : (
            <Export />
          )}
          {!isAlertPartnerAccount ? (
            <>
              <Dropdown menu={{ items: kebabItems, onClick: onApplyMore }} trigger={['click']}>
                <ToggleButton icon={<ToggleIcon />} />
              </Dropdown>
              {isLogsPopupOpen ? (
                <SensitivityLogs isPopupOpen={isLogsPopupOpen} closePopup={closeLogsPopup} />
              ) : null}
            </>
          ) : null}
          <StyledDivider type="vertical" />
          <DatePickerWrapper>
            <DatePickerModifier
              durationAllowed={1}
              interval={[moment(currentInterval[0]), moment(currentInterval[1])]}
              defaultShortcut={shortcut}
              className="ai-alerts-date-picker"
              onApply={onApplyDatePicker}
              onCancel={onCancelDatePicker}
              onChange={onChangeDatePicker}
            />
          </DatePickerWrapper>
        </RightWrapper>
      </HeaderWrapper>
      <DefaultLandingPrompt />
    </HeaderPanelWrapper>
  );
};

export default memo(HeaderPanel);
